import React from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import classNames from 'classnames';
import {
  Layout, Typography, Divider, Button, Spin,
} from 'antd';
import { DoubleLeftOutlined } from '@ant-design/icons';

import AppImage from '../../components/image/AppImage';
import NationalityTag from '../../components/nationality-tag/NationalityTag';

import 'antd/dist/antd.css';
import './ContactProfilePage.css';

function ContactProfilePage({ handleClick, isLoading, contactsCollection }) {
  const { Content } = Layout;
  const { Title, Text } = Typography;

  const contactProfilePage = classNames('ContactProfile');
  const contactProfileBody = classNames('ContactProfile-body');
  const contactProfileImage = classNames('ContactProfile-contactImage');
  const contactProfileInfo = classNames('ContactProfile-contactInfo');
  const contactContactsList = classNames('ContactProfile-contactContactsList');
  const contactContactsItem = classNames('ContactProfile-contactContactsItem');

  const match = useRouteMatch();
  const { params: { contactId } } = match;

  const contactProfile = contactsCollection[contactId - 1];

  if (isLoading) {
    return (
      <Spin />
    );
  }

  return (
    <Content>
      <div className={contactProfilePage}>
        <Title level={1} style={{ fontSize: '32px' }}>Contact View</Title>
        <div className={contactProfileBody}>
          <AppImage className={contactProfileImage} src={contactProfile.picture.medium} />
          <div className={contactProfileInfo}>
            <Title level={3}>
              {`${contactProfile.name.title}. ${contactProfile.name.first} ${contactProfile.name.last}`}
              <Text
                type="secondary"
                style={{ fontSize: '19px', paddingLeft: '5px' }}
              >
                {`(${contactProfile.dob.age} years)`}
              </Text>
            </Title>
            <Divider dashed style={{ margin: '12px 0' }} />

            <ul className={contactContactsList}>
              <li className={contactContactsItem}>
                <Text copyable={{ text: contactProfile.email }} style={{ paddingRight: '8px' }} />
                <a href={`mailto:${contactProfile.email}`}>{contactProfile.email}</a>
              </li>
              <li className={contactContactsItem}>
                <Text copyable={{ text: contactProfile.phone }} style={{ paddingRight: '8px' }} />
                <a href={`tel:${contactProfile.phone}`}>{contactProfile.phone}</a>
              </li>
              <li className={contactContactsItem}>
                <Text
                  copyable={{
                    text: `[${contactProfile.location.country}]
                             ${contactProfile.location.street.number}
                             ${contactProfile.location.street.name},
                             ${contactProfile.location.city},
                             ${contactProfile.location.state}
                             ${contactProfile.location.postcode}`,
                  }}
                  style={{ paddingRight: '8px' }}
                />
                <Text strong style={{ color: '#5b5b6b' }}>{`/${contactProfile.location.country}/`}</Text>
                <br />
                {' '}
                <Text type="secondary" style={{ color: '#5b5b6b' }} ellipsis>
                  {`${contactProfile.location.street.number}
                      ${contactProfile.location.street.name},
                      ${contactProfile.location.city},
                      ${contactProfile.location.state}
                      ${contactProfile.location.postcode}`}
                </Text>
              </li>
            </ul>

            <Divider dashed style={{ margin: '12px 0' }} />
            <NationalityTag userNationality={contactProfile.nat} />
          </div>
        </div>
      </div>

      <Button
        type="primary"
        onClick={handleClick}
        icon={<DoubleLeftOutlined />}
        style={{ marginTop: 15 }}
      >
        Back
      </Button>

    </Content>
  );
}

ContactProfilePage.propTypes = {
  handleClick: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  contactsCollection: PropTypes.arrayOf(PropTypes.shape({
    gender: PropTypes.string,
    name: {
      title: PropTypes.string,
      first: PropTypes.string,
      last: PropTypes.string,
    },
    location: {
      street: {
        number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
      },
      city: PropTypes.string,
      state: PropTypes.string,
      postcode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      coordinates: {
        latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      },
      timezone: {
        offset: PropTypes.string,
        description: PropTypes.string,
      },
    },
    email: PropTypes.string,
    login: {
      uuid: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      username: PropTypes.string,
      password: PropTypes.string,
      salt: PropTypes.string,
      md5: PropTypes.string,
      sha1: PropTypes.string,
      sha256: PropTypes.string,
    },
    dob: {
      date: PropTypes.string,
      age: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    },
    registered: {
      date: PropTypes.string,
      age: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    },
    phone: PropTypes.string,
    cell: PropTypes.string,
    id: {
      name: PropTypes.string,
      value: PropTypes.string,
    },
    picture: {
      large: PropTypes.string,
      medium: PropTypes.string,
      thumbnail: PropTypes.string,
    },
    nat: PropTypes.string,
  })),
};

ContactProfilePage.defaultProps = {
  isLoading: false,
  contactsCollection: [
    {
      gender: 'male',
      name: {
        title: 'mr',
        first: 'brad',
        last: 'gibson',
      },
      location: {
        street: { number: 5421, name: 'Avenue Paul Eluard' },
        city: 'kilcoole',
        state: 'waterford',
        postcode: '93027',
        coordinates: {
          latitude: '20.9267',
          longitude: '-7.9310',
        },
        timezone: {
          offset: '-3:30',
          description: 'Newfoundland',
        },
      },
      email: 'brad.gibson@example.com',
      login: {
        uuid: '155e77ee-ba6d-486f-95ce-0e0c0fb4b919',
        username: 'silverswan131',
        password: 'firewall',
        salt: 'TQA1Gz7x',
        md5: 'dc523cb313b63dfe5be2140b0c05b3bc',
        sha1: '7a4aa07d1bedcc6bcf4b7f8856643492c191540d',
        sha256: '74364e96174afa7d17ee52dd2c9c7a4651fe1254f471a78bda0190135dcd3480',
      },
      dob: {
        date: '1993-07-20T09:44:18.674Z',
        age: 26,
      },
      registered: {
        date: '2002-05-21T10:59:49.966Z',
        age: 17,
      },
      phone: '011-962-7516',
      cell: '081-454-0666',
      id: {
        name: 'PPS',
        value: '0390511T',
      },
      picture: {
        large: 'https://randomuser.me/api/portraits/men/75.jpg',
        medium: 'https://randomuser.me/api/portraits/med/men/75.jpg',
        thumbnail: 'https://randomuser.me/api/portraits/thumb/men/75.jpg',
      },
      nat: 'IE',
    },
  ],
};

export default ContactProfilePage;
